import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "../components/toast";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { usePremisesList } from "../hooks/usePremisesList";
import { PermissionGate, usePermission } from "../auth/access";
import { useScope } from "../context/ScopeContext";
import {
  premiseTypeChips,
  productsForPremise,
  type ProductKey,
} from "../nav/products";
import { vendorFor } from "../utils/devicePresentation";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import HealthChip from "../components/HealthChip";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { isDemoCompanyName } from "@shared/demo";
import { FONT_MONO } from "../theme";

export default function PremisesPage() {
  const { premises, loading, error, refresh } = usePremisesList();
  const navigate = useNavigate();
  // The server refuses both to anyone but a platform admin.
  const canCreate = usePermission("premises", "administrate", "nenda");
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  // Type-column sort: null = unsorted (default order), else asc/desc by segment.
  const [typeOrder, setTypeOrder] = useState<"asc" | "desc" | null>(null);
  const [deleting, setDeleting] = useState<PremiseSummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);

  // The customer/location narrowing lives in the header scope bar. A
  // ?company= deep-link from the Companies list is folded into it on arrival,
  // so there is only ever one filter in force. "unassigned" is the exception:
  // a synthetic value (premises with no Company) the scope bar cannot express.
  const { scope, setScope } = useScope();
  const unassignedOnly = searchParams.get("company") === "unassigned";

  useEffect(() => {
    foyerApi
      .listCompanies()
      .then(setCompanies)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const deepLink = searchParams.get("company");
    if (!deepLink || deepLink === "unassigned") return;
    setScope({ companyId: deepLink });
    const next = new URLSearchParams(searchParams);
    next.delete("company");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setScope]);

  function clearCompanyFilter() {
    if (unassignedOnly) {
      const next = new URLSearchParams(searchParams);
      next.delete("company");
      setSearchParams(next);
      return;
    }
    setScope({ companyId: "" });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = premises;
    if (unassignedOnly) list = list.filter((p) => !p.companyId);
    else if (scope.companyId) {
      list = list.filter((p) => p.companyId === scope.companyId);
    }
    if (scope.product) {
      list = list.filter((p) =>
        productsForPremise(p).has(scope.product as ProductKey)
      );
    }
    if (q) {
      list = list.filter(
        (p) =>
          p.premiseName.toLowerCase().includes(q) ||
          p.premiseId.toLowerCase().includes(q) ||
          (p.models ?? []).some((m) => m.toLowerCase().includes(q))
      );
    }
    if (typeOrder) {
      const dir = typeOrder === "asc" ? 1 : -1;
      const key = (x: PremiseSummary) =>
        premiseTypeChips(x)
          .map((c) => c.label)
          .join(", ");
      list = [...list].sort(
        (a, b) =>
          key(a).localeCompare(key(b)) * dir ||
          a.premiseName.localeCompare(b.premiseName)
      );
    }
    return list;
    // NB: scope.premiseId deliberately does NOT filter here. This list is
    // where you pick a location - narrowing it to the one already picked
    // leaves you a one-row table and no way back out.
  }, [
    premises,
    query,
    unassignedOnly,
    scope.companyId,
    scope.product,
    typeOrder,
  ]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("premisesPage.eyebrow")}
        title={t("premisesPage.title")}
        actions={
          <>
            {/* The customer narrowing lives in the header's Customer selector
                now - a second chip for it just crowded the same row. Only
                "unassigned" still needs one: the selector cannot express it. */}
            {unassignedOnly && (
              <Chip
                label={t("premisesPage.unassigned")}
                onDelete={clearCompanyFilter}
                deleteIcon={<ClearIcon />}
                variant="outlined"
                data-testid="premises-company-filter"
              />
            )}
            {canCreate && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                data-testid="premises-new"
              >
                {t("premisesPage.newPremise")}
              </Button>
            )}
          </>
        }
      />
      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("premisesPage.searchPlaceholder")}
        size="small"
        fullWidth
        inputProps={{ "data-testid": "premise-search" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {loading && <TableSkeleton rows={6} />}
      {error && (
        <Typography color="error" data-testid="premises-error">
          {error.message}
        </Typography>
      )}
      {!loading && !error && filtered.length === 0 && (
        <Typography color="text.secondary">
          {query
            ? t("premisesPage.noMatch", { query })
            : t("premisesPage.empty")}
        </Typography>
      )}
      {filtered.length > 0 && (
        <TableContainer>
          <Table size="small" data-testid="premises-list">
            <TableHead>
              <TableRow>
                <TableCell>{t("premisesPage.columns.name")}</TableCell>
                <TableCell sortDirection={typeOrder ?? false}>
                  <TableSortLabel
                    active={typeOrder !== null}
                    direction={typeOrder ?? "asc"}
                    onClick={() =>
                      setTypeOrder((o) => (o === "asc" ? "desc" : "asc"))
                    }
                  >
                    {t("premisesPage.columns.type")}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t("premisesPage.columns.id")}</TableCell>
                <TableCell align="right">
                  {t("premisesPage.columns.units")}
                </TableCell>
                <TableCell>{t("premisesPage.columns.health")}</TableCell>
                <TableCell align="right">
                  {t("premisesPage.columns.online")}
                </TableCell>
                <TableCell align="right">
                  {t("premisesPage.columns.licenses")}
                </TableCell>
                <TableCell>{t("premisesPage.columns.displays")}</TableCell>
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.premiseId}
                  hover
                  onClick={() => navigate(`/premises/${p.premiseId}`)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`premise-${p.premiseId}`}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="fontWeightMedium">
                      {p.premiseName}
                    </Typography>
                  </TableCell>
                  <TableCell data-testid={`premise-${p.premiseId}-segment`}>
                    {premiseTypeChips(p).length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        {t("premisesPage.none")}
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        {premiseTypeChips(p).map((c) => (
                          <Chip
                            key={c.label}
                            size="small"
                            label={c.label}
                            color="info"
                            variant={c.kind === "addon" ? "outlined" : "filled"}
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily={FONT_MONO}
                    >
                      {p.premiseId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{p.roomCount}</Typography>
                  </TableCell>
                  <TableCell>
                    <HealthChip
                      health={p.health}
                      testId={`premise-${p.premiseId}-health`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      color={p.onlineCount > 0 ? "success" : "default"}
                      label={`${p.onlineCount}/${p.screenCount}`}
                      data-testid={`premise-${p.premiseId}-online`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {p.licenses ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            p.licenses.unlimited
                              ? "success"
                              : p.licenses.hasFreeSeats
                                ? "primary"
                                : "error"
                          }
                          label={
                            p.licenses.unlimited
                              ? `${p.licenses.activatedCount} ∞`
                              : `${p.licenses.activatedCount}/${p.licenses.totalSeats}`
                          }
                          data-testid={`premise-${p.premiseId}-licenses`}
                        />
                        {p.licenses.demoCount > 0 && (
                          <Tooltip title={t("premisesPage.demoSeatsHint")}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t("premisesPage.demoSeats", {
                                count: p.licenses.demoCount,
                              })}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {/* `?? []` for the same reason as the overview cards: a
                          summary row that arrives without `models` must render
                          as "none", not throw and blank the whole page. */}
                      {(p.models ?? []).length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          {t("premisesPage.none")}
                        </Typography>
                      ) : (
                        // Vendors, not model strings: the same rollup the
                        // premise overview shows, and it fits on one line.
                        [...new Set((p.models ?? []).map(vendorFor))].map(
                          (v) => (
                            <Tooltip
                              key={v}
                              title={(p.models ?? [])
                                .filter((m) => vendorFor(m) === v)
                                .join(", ")}
                            >
                              <Chip size="small" variant="outlined" label={v} />
                            </Tooltip>
                          )
                        )
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <PermissionGate
                      resource="premises"
                      level="administrate"
                      place={{ premiseId: p.premiseId }}
                    >
                      <Tooltip title={t("premisesPage.delete.title")}>
                        <IconButton
                          size="small"
                          aria-label={t("premisesPage.delete.named", {
                            name: p.premiseName,
                          })}
                          onClick={() => {
                            setDeleting(p);
                          }}
                          data-testid={`premise-delete-${p.premiseId}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ height: 24 }} />

      <CreatePremiseDialog
        open={createOpen}
        companies={companies}
        defaultCompanyId={unassignedOnly ? "" : scope.companyId}
        onClose={() => setCreateOpen(false)}
        onCreated={(slug, name) => {
          setCreateOpen(false);
          toast.success(t("premisesPage.created", { name }));
          refresh();
          navigate(`/premises/${slug}`);
        }}
        onError={(msg) => toast.error(msg)}
      />

      <DangerConfirmDialog
        open={!!deleting}
        title={t("premisesPage.delete.title")}
        entityName={
          deleting ? `${deleting.premiseName} (${deleting.premiseId})` : ""
        }
        body={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("premisesPage.delete.intro")}
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                paddingLeft: 18,
                color: "text.secondary",
                typography: "bodySm",
              }}
            >
              <li>
                {t("premisesPage.delete.rooms", {
                  count: deleting?.roomCount ?? 0,
                })}
              </li>
              <li>
                {t("premisesPage.delete.screens", {
                  count: deleting?.screenCount ?? 0,
                })}
              </li>
              <li>
                {t("premisesPage.delete.licenses", {
                  count: deleting?.licenses?.totalSeats ?? 0,
                })}
              </li>
              <li>{t("premisesPage.delete.codes")}</li>
            </Box>
          </Box>
        }
        busy={deleteBusy}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleting(null);
        }}
        onConfirm={async () => {
          if (!deleting) return;
          setDeleteBusy(true);
          try {
            await foyerApi.deletePremise(deleting.premiseId);
            toast.success(
              t("premisesPage.deleted", { name: deleting.premiseName })
            );
            setDeleting(null);
            refresh();
          } catch (e) {
            // The dialog stays open so the delete can be retried.
            toast.error(
              describeError(t, "deletePremise", e, {
                name: deleting.premiseName,
              })
            );
          } finally {
            setDeleteBusy(false);
          }
        }}
      />
    </PageContainer>
  );
}

/** Client-side slug preview; the server validates + owns the final value. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CreatePremiseDialog({
  open,
  companies,
  defaultCompanyId,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  companies: CompanySummary[];
  defaultCompanyId: string;
  onClose: () => void;
  onCreated: (slug: string, name: string) => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setSlugError(null);
      setCompanyId(defaultCompanyId === "unassigned" ? "" : defaultCompanyId);
    }
  }, [open, defaultCompanyId]);

  // The slug auto-tracks the name until the user edits it directly.
  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid =
    effectiveSlug.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(effectiveSlug);

  async function submit() {
    setBusy(true);
    setSlugError(null);
    try {
      const created = await foyerApi.createPremise({
        name,
        slug: effectiveSlug,
        companyId,
      });
      onCreated(created.premiseId, name);
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === "slug_already_exists") {
        setSlugError(
          t("premisesPage.create.slugTaken", { slug: effectiveSlug })
        );
      } else if (code === "invalid_slug") {
        setSlugError(t("premisesPage.create.slugInvalid"));
      } else {
        onError(describeError(t, "createPremise", e, { name }));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("premisesPage.newPremise")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("premisesPage.create.displayName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            size="small"
            helperText={t("premisesPage.create.displayNameHelp")}
            inputProps={{ "data-testid": "premise-name" }}
          />
          <TextField
            label={t("premisesPage.create.slug")}
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
              setSlugError(null);
            }}
            size="small"
            error={(!!effectiveSlug && !slugValid) || !!slugError}
            helperText={
              slugError
                ? slugError
                : !!effectiveSlug && !slugValid
                  ? t("premisesPage.create.slugFormat")
                  : t("premisesPage.create.slugHelp")
            }
            inputProps={{
              "data-testid": "premise-slug",
              style: { fontFamily: FONT_MONO },
            }}
          />
          <TextField
            select
            label={t("premisesPage.create.company")}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
            size="small"
            helperText={
              isDemoCompanyName(companies.find((c) => c.id === companyId)?.name)
                ? t("premisesPage.create.demoCompanyHelp")
                : t("premisesPage.create.companyHelp")
            }
            inputProps={{ "data-testid": "premise-company" }}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {isDemoCompanyName(c.name)
                  ? t("premisesPage.create.salesDemo", { name: c.name })
                  : c.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={busy || !name.trim() || !slugValid || !companyId}
          onClick={submit}
          data-testid="premise-create"
        >
          {busy
            ? t("premisesPage.create.creating")
            : t("common:buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
